<template>
  <div class="w-full">
    <h1 class="mb-8 text-3xl font-bold">
      Specjaliści
    </h1>
    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="specialist-filter-name"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Imię i nazwisko
        </label>
        <input
          id="specialist-filter-name"
          v-model="form.name"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="specialist-filter-specialisation"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Specjalizacja
        </label>
        <input
          id="specialist-filter-specialisation"
          v-model="form.specialisation"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
      <Link
        class="btn-indigo ml-auto"
        :href="routes.new()"
      >
        Dodaj specjalistę
      </Link>
    </div>
    <div class="overflow-x-auto rounded-sm bg-white shadow-sm">
      <table
        class="w-full whitespace-nowrap"
        :class="{ 'is-dnd-dragging': canReorder && dragging }"
      >
        <thead>
          <tr class="text-left font-bold">
            <th class="w-px px-4 pb-4 pt-6">
              <!-- Drag handle column -->
            </th>
            <th class="px-6 pb-4 pt-6 text-right text-green-700">
              Tytuł
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Imię i nazwisko
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Specjalizacje
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Dane aktualizacji
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Zdjęcie
            </th>
            <th class="px-6 pb-4 pt-6 text-center">
              <specialist-content-column-header />
            </th>
            <th class="px-6 pb-4 pt-6">
              Aktywny
            </th>
            <th class="px-6 pb-4 pt-6">
              <!-- Actions -->
            </th>
          </tr>
        </thead>
        <draggable
          v-if="localSpecialists.length"
          v-model="localSpecialists"
          tag="tbody"
          item-key="id"
          handle=".drag-handle"
          ghost-class="dnd-drop-ghost"
          chosen-class="dnd-chosen"
          :disabled="!canReorder"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: specialist }">
            <tr
              :key="specialist.id"
              :class="specialist.manual ? customRowClass : 'focus-within:bg-gray-100 hover:bg-gray-100'"
            >
              <td class="w-px border-t px-4">
                <div
                  v-if="canReorder"
                  class="drag-handle cursor-grab active:cursor-grabbing"
                  title="Przeciągnij, aby zmienić kolejność"
                >
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                  </svg>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center justify-end px-6 py-4">
                  <span class="max-w-md truncate text-right">{{ specialist.title || '—' }}</span>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center gap-2 px-6 py-4">
                  {{ specialist.full_name }}
                  <span
                    v-if="specialist.manual"
                    :class="customBadgeClass"
                    title="Specjalista dodany ręcznie (poza Medfile)"
                  >
                    <icon :name="customIcon" class="h-4 w-4" />
                    {{ customLabel }}
                  </span>
                </div>
              </td>
              <td class="border-t">
                <div class="flex flex-wrap items-center gap-2 px-6 py-4">
                  <specialisation-badge
                    v-for="spec in specialist.specialisations"
                    :key="spec.id"
                    :name="spec.name"
                    :site-context="spec.site_context"
                  />
                  <span
                    v-if="!specialist.specialisations || !specialist.specialisations.length"
                    class="text-gray-400"
                  >
                    —
                  </span>
                </div>
              </td>
              <td class="border-t">
                <record-update-dates
                  :synced-at="specialist.synced_at"
                  :updated-at="specialist.updated_at"
                />
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <img
                    v-if="specialist.photo_url"
                    :src="specialist.photo_url"
                    :alt="specialist.full_name"
                    class="h-12 w-12 rounded-full object-cover"
                  >
                  <span v-else class="text-gray-400">Brak zdjęcia</span>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center justify-center px-6 py-4">
                  <content-missing-dot :items="specialist.missing_content || []" />
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <toggle-switch
                    :model-value="specialist.active"
                    :disabled="togglingId === specialist.id"
                    @update:model-value="toggleActive(specialist)"
                  />
                </div>
              </td>
              <td class="w-px border-t">
                <div class="ml-4 flex shrink-0 items-center gap-2 px-4">
                  <a
                    v-if="specialist.id"
                    class="btn-action-view"
                    :href="$routes.specialist(specialist.slug)"
                    target="_blank"
                    title="Zobacz na stronie"
                  >
                    <icon name="eye" class="mr-1.5 h-3.5 w-3.5" />
                    Podgląd na stronie
                  </a>
                  <Link
                    v-if="specialist.id"
                    class="btn-action-edit"
                    :href="editRoute(specialist.id)"
                    title="Edytuj"
                  >
                    <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Edytuj
                  </Link>
                  <button
                    v-if="specialist.id && !specialist.remote_uuid"
                    type="button"
                    class="btn-action-delete"
                    title="Usuń"
                    @click.stop="destroy(specialist)"
                  >
                    <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </draggable>
        <tbody v-else>
          <tr>
            <td class="border-t px-6 py-4" colspan="9">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p
      v-if="!canReorder"
      class="mt-3 text-xs text-gray-500"
    >
      Aby zmienić kolejność, wyczyść filtry.
    </p>
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import pickBy from 'lodash/pickBy';
import throttle from 'lodash/throttle';
import { createSpecialistRouteHelper } from '@/utils/createSpecialistRouteHelper';
import { createLayoutHelper } from '@/utils/layoutHelper';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue';
import ContentMissingDot from '@/Shared/ContentMissingDot.vue';
import SpecialistContentColumnHeader from '@/Shared/SpecialistContentColumnHeader.vue';
import RecordUpdateDates from '@/Shared/RecordUpdateDates.vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import {
  CUSTOM_RESOURCE_ICON,
  CUSTOM_RESOURCE_LABEL,
  CUSTOM_RESOURCE_BADGE_CLASS,
  CUSTOM_RESOURCE_ROW_CLASS,
} from '@/utils/customResourceStyle';

export default {
  components: {
    Link,
    Icon,
    ToggleSwitch,
    SpecialisationBadge,
    ContentMissingDot,
    SpecialistContentColumnHeader,
    RecordUpdateDates,
    draggable,
  },
  layout: createLayoutHelper,
  props: {
    specialists: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      customIcon: CUSTOM_RESOURCE_ICON,
      customLabel: CUSTOM_RESOURCE_LABEL,
      customBadgeClass: CUSTOM_RESOURCE_BADGE_CLASS,
      customRowClass: CUSTOM_RESOURCE_ROW_CLASS,
      form: {
        name: this.filters.name || '',
        specialisation: this.filters.specialisation || '',
      },
      localSpecialists: [...this.specialists.data],
      dragging: false,
      togglingId: null,
    };
  },
  computed: {
    routes() {
      return createSpecialistRouteHelper(null, this.$routes);
    },
    hasActiveFilters() {
      return !!(this.form.name?.trim() || this.form.specialisation?.trim());
    },
    // Filtered subset must not rewrite global order positions.
    canReorder() {
      return !this.hasActiveFilters;
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak specjalistów pasujących do filtrów.'
        : 'Nie znaleziono specjalistów.';
    },
  },
  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy({
          name: this.form.name?.trim(),
          specialisation: this.form.specialisation?.trim(),
        });
        this.$inertia.get(
          this.routes.index(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['specialists'],
          },
        );
      }, 150),
      deep: true,
    },
    'specialists.data': {
      handler(newData) {
        // Update local specialists when data changes
        this.localSpecialists = [...newData];
      },
      deep: true,
    },
  },
  methods: {
    resetFilters() {
      this.form.name = '';
      this.form.specialisation = '';
    },
    editRoute(specialistId) {
      return specialistId ? this.routes.edit(specialistId) : '#';
    },
    toggleActive(specialist) {
      if (!specialist.id || this.togglingId) return;
      this.togglingId = specialist.id;
      const target = this.localSpecialists.find((item) => item.id === specialist.id);
      if (target) target.active = !target.active;

      this.$inertia.put(
        this.routes.update(specialist.id),
        { specialist: { active: target ? target.active : !specialist.active } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['specialists'],
          onError: () => {
            if (target) target.active = !target.active;
          },
          onFinish: () => {
            this.togglingId = null;
          },
        },
      );
    },
    destroy(specialist) {
      if (!specialist.id) return;
      const message = 'Czy na pewno chcesz usunąć tego manualnego specjalistę?\n\n'
        + 'Usunięte zostaną jego zaplanowane sloty i powiązania z usługami '
        + '(same usługi zostają). Historia wizyt pozostanie, ale bez przypisanego specjalisty.';
      if (confirm(message)) {
        this.$inertia.delete(this.routes.destroy(specialist.id), {
          preserveScroll: true,
        });
      }
    },
    async onDragEnd() {
      this.dragging = false;
      if (!this.canReorder) return;

      const ids = this.localSpecialists.map(specialist => specialist.id);
      // Absolute order start for the visible slice (0 when the full list is shown).
      const offset = Math.max(0, (this.specialists.meta?.from || 1) - 1);

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(this.routes.reorder(), {
          ids,
          offset,
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        this.$inertia.reload({ only: ['specialists'] });
      } catch (error) {
        console.error('Failed to reorder specialists:', error);
        this.localSpecialists = [...this.specialists.data];
        alert('Nie udało się zmienić kolejności specjalistów. Spróbuj ponownie.');
      }
    },
    onDragStart() {
      this.dragging = true;
    },
  },
};
</script>
